import React from "react";
import { useTranslation } from "@lib/i18n";

import Typography from "@components/shared/Typography";
import DownloadIcon from "@components/shared/Icons/Download";
import { Button } from "@components/shared/Button";
import CollapsableContainer from "@components/shared/CollapsableContainer";

import gtm from "@gtm/core";
import { getStaticAssetURL } from "@lib/getStaticAssetURL";
import { openDocument } from "@lib/openDocument";
import { useGetAssetPath } from "hooks/useGetAssetPath";
import { PDF_DOCUMENT_TYPES } from "@shared/constants";
import { useActiveCarPreconfigurationSelector } from "@redux/reducers/deal";

import {
  displayTechnicalDetailsEvent,
  openCustomizeSectionEvent,
} from "@gtm/events/homepage";
import Icon from "@components/shared/Icons/Icon";

const TechnicalDetails = () => {
  const { t, i18n } = useTranslation();
  const getAssetPath = useGetAssetPath(i18n.language);

  const activePreconfiguration = useActiveCarPreconfigurationSelector();

  const openTechnicalDetails = () => {
    gtm.fire(displayTechnicalDetailsEvent());
    openDocument(getAssetPath(PDF_DOCUMENT_TYPES.TECHNICAL_CHARACTERISTICS));
  };

  const gtmEvent = prevState => {
    if (!prevState) {
      gtm.fire(openCustomizeSectionEvent(t("technicalDetails.title")));
    }
  };

  return (
    <CollapsableContainer
      data-id="technical-details-open"
      title={t("technicalDetails.title")}
      onChange={gtmEvent}
    >
      <div className="flex flex-col items-center gap-4 w-full">
        {t("technicalDetails.importantInformation") && (
          <Button
            className="mb-4"
            onClick={() =>
              openDocument(
                getAssetPath(PDF_DOCUMENT_TYPES.IMPORTANT_INFORMATION)
              )
            }
            size="small"
            variant="secondary"
          >
            <Icon
              className="size-6 mr-2"
              icon={<DownloadIcon data-id="technical-details-icon-download" />}
            />
            {t("technicalDetails.importantInformation")}
          </Button>
        )}
        <div className="w-full relative" data-id="technical-details-blueprint">
          <img
            alt={t("technicalDetails.dimensions")}
            className="w-full object-contain"
            src={getStaticAssetURL("/images/blueprint.svg")}
          />
          <div className="w-12 h-3 absolute top-[93.4%] left-[79.75%] -translate-1/2 text-center text-sm/1">
            {activePreconfiguration.width} {t("general.meterSymbol")}
          </div>

          <div className="w-12 h-3 absolute top-[79%] left-[61.7%] -translate-1/2 rotate-90 text-center text-sm/1">
            {activePreconfiguration.height} {t("general.meterSymbol")}
          </div>
          <div className="w-12 h-3 absolute top-[93.4%] left-[29.8%] -translate-1/2 text-center text-sm/1">
            {activePreconfiguration.length} {t("general.meterSymbol")}
          </div>
        </div>
        <div
          className="flex flex-col gap-4 w-full"
          data-id="technical-details-info"
        >
          {activePreconfiguration.technicalCharacteristics.map(
            ({ label, description }, key) => (
              <div className="flex justify-between w-full" key={key}>
                <Typography className="text-sm uppercase font-semibold">
                  {label}
                </Typography>
                <Typography className="text-sm">{description}</Typography>
              </div>
            )
          )}
        </div>
        <div
          className="flex flex-col gap-2 w-full"
          data-id="technical-details-info"
        >
          <Typography className="text-sm uppercase font-semibold mt-8">
            {t("technicalDetails.descriptionTitle")}
          </Typography>
          <Typography className="text-sm/normal">
            {t("technicalDetails.description")}
          </Typography>
        </div>
        <img
          alt={t("general.technicalCharacteristicsLabel", {
            preconfigurationLabel: activePreconfiguration.label,
          })}
          className="w-full rounded-md"
          src={getStaticAssetURL(
            "/images/gallery/technical-characteristics/additionnal.jpg"
          )}
          title={t("general.technicalCharacteristicsLabel", {
            preconfigurationLabel: activePreconfiguration.label,
          })}
        />
        <Button
          className="mt-4"
          onClick={openTechnicalDetails}
          variant="secondary"
        >
          <Icon
            className="size-6 mr-2"
            icon={<DownloadIcon data-id="technical-details-icon-download" />}
          />
          {t("technicalDetails.techList")}
        </Button>
      </div>
    </CollapsableContainer>
  );
};

export default TechnicalDetails;
